import { toast } from 'react-toastify';
import { combineEpics, ofType } from "redux-observable";
import { from, of, concat } from "rxjs";
import { map, catchError, mergeMap, debounceTime, withLatestFrom, concatMap, tap, distinctUntilChanged, switchMap } from "rxjs/operators";
import { importWellSurveyData, wellSurveyService } from "../../../services/jetlift/jetlift-output/wellSurvey.service";
import { getTailPipeTZ } from "../../../services/jetlift/jetlift-output/basicCals.Service";
import { setLoading, setError, setWellSurveyData, clearDataSuccess, setWellKOPOverride } from "../../../store/jetlift/jetlift-output/WellSurveySlice";
import { RootState } from '../../../store/ConfigureStore';
import * as wellSurvey from "../../../../src/store/jetlift/jetlift-output/WellSurveySlice";

const fetchWellSurveyEpic = (action$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/fetchData"), // Triggered by the action type
        mergeMap((action: any) => {
            const { inputData } = action.payload;
            return from(importWellSurveyData(inputData)).pipe(
                tap((response: any) => {
                    toast.success('Successfully imported survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#4CAF50',
                            color: '#fff'
                        }
                    });
                }),
                map((response: any) => {
                    // Handle nested response structure with surveyData property
                    const dataArray = response?.surveyData || response?.data || (Array.isArray(response) ? response : []);
                    const processedData = dataArray.map((item: any, index: number) => ({
                        ...item,
                        id: item.id || index + 1
                    }));
                    return { type: "jetlift/wellSurvey/fetchDataSuccess", payload: processedData };
                }),
                catchError((error) => {
                    toast.error('Failed to import survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#f44336',
                            color: '#fff'
                        }
                    });
                    return of(setError(error.message));
                })
            );
        })
    );
};



const clearWellSurveyEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/clearData"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject)),
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.isWellSurveyDataPersisted))
        ),
        mergeMap(([actionData, selectedProject, isDataPersisted]) => {
            // If data is not persisted in DB, just clear locally
            if (!isDataPersisted) {
                toast.success('Successfully cleared survey data', {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    style: {
                        background: '#4CAF50',
                        color: '#fff'
                    }
                });
                return of({ type: "jetlift/wellSurvey/clearDataSuccess" });
            }

            // If data is persisted, clear from DB
            let url = window.location.href;
            let projectIdFromUrl = null;
            const projectIdRegex = /projectID=([\w-]+)/;
            const match = url.match(projectIdRegex);
            if (match && match[1]) {
                projectIdFromUrl = match[1];
            }
            const projectId = projectIdFromUrl
                ? projectIdFromUrl
                : (actionData?.payload?.projectId
                    ? actionData?.payload?.projectId
                    : selectedProject?.projectID || selectedProject?.caseID);

            if (!projectId) {
                return of({ type: "jetlift/wellSurvey/clearExistingDataError", payload: "No project ID found" });
            }

            return from(wellSurveyService.clearWellSurveyData(projectId)).pipe(
                tap(() => {
                    toast.success('Successfully cleared survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#4CAF50',
                            color: '#fff'
                        }
                    });
                }),
                mergeMap(() => concat(
                    of({ type: "jetlift/wellSurvey/clearDataSuccess" }),
                    of({ type: "jetlift/wellSurvey/fetchExistingData" }),
                )),
                catchError((error) => {
                    toast.error('Failed to clear survey data', {
                        position: 'top-right',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        style: {
                            background: '#f44336',
                            color: '#fff'
                        }
                    });
                    return of(setError(error.message));
                })
            );
        })
    );
}


const fetchExistingWellSurveyEpic = (action$: any, state$: any) =>
    action$.pipe(
        ofType("jetlift/wellSurvey/fetchExistingData"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject))
        ),
        switchMap(([actionData, selectedProject]) => {
            const projectId: string = actionData?.payload?.projectId
                ? actionData?.payload?.projectId
                : selectedProject?.projectID || selectedProject?.caseID;

            if (!projectId) {
                return of({ type: "jetlift/wellSurvey/fetchExistingDataError", payload: "No project ID found" });
            }

            return from(wellSurveyService.getExistingWellSurveyData(projectId)).pipe(
                tap((response) => { }),
                mergeMap((response: any) => {
                    // Handle case where response might not be an array
                    const dataArray = Array.isArray(response) ? response : ((response as any)?.data || []);
                    const actions = [
                        {
                            type: "jetlift/wellSurvey/fetchExistingDataSuccess",
                            payload: dataArray
                        },
                        {
                            type: "jetlift/wellSurvey/setWellSurveyDataPersisted",
                            payload: dataArray && dataArray.length > 0
                        }
                    ];
                    
                    // If response has metadata, restore all metadata values
                    if (response?.metadata) {
                        const metadata = response.metadata;
                        if (metadata.KOPOverride) {
                            actions.push({ type: "jetlift/wellSurvey/setWellKOPOverride", payload: metadata.KOPOverride });
                        }
                        if (metadata.MaxDLSAboveKOP) {
                            actions.push({ type: "jetlift/wellSurvey/setMaxDLSAboveKOP", payload: metadata.MaxDLSAboveKOP });
                        }
                        if (metadata.TransitionZone) {
                            actions.push({ type: "jetlift/wellSurvey/setTransitionZone", payload: metadata.TransitionZone });
                        }
                        if (metadata.ShowWellDataInMainReport !== undefined) {
                            actions.push({ type: "jetlift/wellSurvey/setWellDataInMainReportFlag", payload: metadata.ShowWellDataInMainReport });
                        }
                        if (metadata.Notes) {
                            actions.push({ type: "jetlift/wellSurvey/setNotes", payload: metadata.Notes });
                        }
                    }
                    
                    return from(actions);
                }),
                catchError((error) => {
                    console.error('Error fetching well survey data:', error);
                    return of({
                        type: "jetlift/wellSurvey/fetchExistingDataError",
                        payload: error.message
                    });
                })
            );
        })
    );


const calculateMaxDLS = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/setWellKOPOverride", "jetlift/wellSurvey/fetchExistingDataSuccess", "jetlift/wellSurvey/fetchDataSuccess"),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.KOPOverride)),
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.wellSurveyData))
        ),
        tap(([action, kopOverride, wellSurveyData]: [{ type: string }, number | undefined, any[]]) => {
        }),
        map(([actionData, kopOverride, wellSurvey]) => {
            if (!wellSurvey || !kopOverride || wellSurvey.length === 0) {
                return 0;
            }

            const kopIndex = wellSurvey.findIndex((x: any) => x?.md === kopOverride);

            if (kopIndex === -1) {
                return 0;
            }

            const arr = wellSurvey.slice(0, kopIndex);
            const maxDLS = Math.max(...arr.map((o: any) => o.dls || 0));

            return maxDLS;
        }),

        map(maxDLS => wellSurvey.setMaxDLSAboveKOP(maxDLS))
    );
};

const calculateDepthMaxDLS = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/setMaxDLSAboveKOP"),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.MaxDLSAboveKOP)),
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.wellSurveyData))
        ),
        tap(([action, maxDLS, wellSurveyData]) => {
        }),
        map(([actionData, maxDLS, wellSurvey]) => {
            try {
                if (wellSurvey?.length > 0 && maxDLS) {
                    const mdIndex = wellSurvey.findIndex((x: any) => x.dls === maxDLS);
                    if (mdIndex === -1) return 0;

                    const depthMD = wellSurvey[mdIndex]?.md;
                    return depthMD;
                }
                return 0;
            } catch (error) {
                console.error('Error calculating DepthMaxDLS:', error);
                return 0;
            }
        }),

        map(depthMD => wellSurvey.setDepthOfMaxDLS(depthMD))
    );
};

const calculateTransitionZone = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/setWellKOPOverride", "jetlift/wellSurvey/fetchExistingDataSuccess", "jetlift/wellSurvey/fetchDataSuccess"),
        debounceTime(100),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.wellSurveyData)),
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.KOPOverride))
        ),
        distinctUntilChanged((prev: [any, any[], number | undefined], curr: [any, any[], number | undefined]) => {
            const prevKOP = prev[2];
            const currKOP = curr[2];
            const prevDataLength = prev[1]?.length || 0;
            const currDataLength = curr[1]?.length || 0;
            const isSame = prevKOP === currKOP && prevDataLength === currDataLength;
            return isSame;
        }),
        map(([actionData, wellSurvey, kopOverride]) => {
            try {
                if (wellSurvey?.length > 0 && kopOverride) {
                    const mdIndex = wellSurvey.findIndex((x: any) => x?.md === kopOverride);
                    
                    if (mdIndex === -1) {
                        return 0;
                    }
                    
                    const transitionZone = wellSurvey[mdIndex - 1]?.md;
                    return transitionZone || 0;
                }
                return 0;
            } catch (error) {
                console.error('Error calculating Transition Zone:', error);
                return 0;
            }
        }),
        map((tzValue: number) => wellSurvey.setTransitionZone(tzValue))
    );
};

interface TailPipe {
    tailPipeDesander: number;
    transitionZone: number;
    tailPipeBttm: number;
}

const calculateTailPipeTZ = (action$: any, state$: any) => {
    return action$.pipe(
        // Only trigger on specific actions that should cause recalculation
        ofType(
            "jetlift/wellSurvey/setTailPipeDesander",
            "jetlift/wellSurvey/setTailPipeBottom",
            "jetlift/wellSurvey/setTransitionZone",
        ),
        // Add debounce to prevent rapid firing
        debounceTime(1000),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.calculations.schematicData)),
            state$.pipe(map((state: RootState) => state.wellSurvey.TransitionZone)),
            state$.pipe(map((state: RootState) => state.schematic.ESPEquipmentsForBelow))
        ),
        mergeMap(([action, schematicData, transitionZone, equipmentForBelow]) => {
            try {
                if (schematicData?.setDepth > 0) {
                    const tailPipeDesander = schematicData.setDepth;
                    let tailPipeBttm = 0;
                    const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                    const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                    const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);
                    const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                        ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                        : 0;
                    const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;

                    if (tailPipeLengthValue === 0) {
                        tailPipeBttm = bottomOfEspSensorVal + equipmentBelowLength;
                    } else {
                        tailPipeBttm = bottomOfEspSensorVal + tailPipeLength;
                    }

                    const postData: TailPipe = {
                        tailPipeDesander,
                        transitionZone,
                        tailPipeBttm
                    };

                    // Return concatenated observable of actions
                    return concat(
                        of(wellSurvey.setTailPipeLength(tailPipeLength)),
                        from(getTailPipeTZ(postData)).pipe(
                            map(response => wellSurvey.setTailPipeInsideTZ(Number(response)))
                        )
                    );
                }

                const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);

                // Skip calculation if essential values are missing
                if (!bottomOfEspSensorVal || !transitionZone) {
                    return of(wellSurvey.setTailPipeInsideTZ(0));
                }

                const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                    ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                    : 0;
                const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;
                const tailPipeBttm = tailPipeLengthValue === 0
                    ? bottomOfEspSensorVal + equipmentBelowLength
                    : bottomOfEspSensorVal + tailPipeLength;

                const postData: TailPipe = {
                    tailPipeDesander: schematicData.setDepth,
                    transitionZone,
                    tailPipeBttm
                };

                // Return single concat observable instead of multiple actions
                return concat(
                    of(wellSurvey.setTailPipeLength(tailPipeLength)),
                    of(wellSurvey.setTailPipeBottom(tailPipeBttm)),
                    from(getTailPipeTZ(postData)).pipe(
                        // Add distinctUntilChanged to prevent duplicate values
                        distinctUntilChanged(),
                        map(response => wellSurvey.setTailPipeInsideTZ(Number(response)))
                    )
                ).pipe(
                    // Add catchError here to handle any errors in the chain
                    catchError(error => {
                        console.error('Error in tail pipe calculation:', error);
                        return of(wellSurvey.setTailPipeInsideTZ(0));
                    })
                );
            } catch (error) {
                console.error('Error calculating Tail Pipe TZ:', error);
                return of(wellSurvey.setTailPipeInsideTZ(0));
            }
        })
    );
};

const calculateTailPipeBttmLength = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/wellSurvey/setTailPipeDesander",
            "jetlift/wellSurvey/setBottomOfEspSensor",
            "jetlift/calculations/setSchematicMiscData",
            "jetlift/calculations/setSchematicData"
        ),
        debounceTime(500),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state.calculations.schematicData)),
            state$.pipe(map((state: RootState) => state.schematic.ESPEquipmentsForBelow))
        ),
        map(([action, schematicData, equipmentForBelow]) => {
            try {
                let tailPipeBttmTwoDecimal = 0;
                const bottomOfEspSensorVal = +(schematicData?.sensorDepth || 0);
                const equipmentBelowLength = equipmentForBelow?.[0]?.length ?? 0;
                const tailPipeDesanderVal = +(schematicData?.miscellaneous?.tailPipeJoints || 0);
                const tailPipeLengthValue = !isNaN(tailPipeDesanderVal)
                    ? parseFloat((tailPipeDesanderVal * 31.7).toFixed(1))
                    : 0;
                const tailPipeLength = tailPipeLengthValue + equipmentBelowLength;

                if (tailPipeLengthValue === 0) {
                    tailPipeBttmTwoDecimal = bottomOfEspSensorVal + equipmentBelowLength;
                } else {
                    tailPipeBttmTwoDecimal = bottomOfEspSensorVal + tailPipeLength;
                }

                const tailPipeBttm = parseFloat(tailPipeBttmTwoDecimal.toFixed(2));
                return wellSurvey.setTailPipeBottom(tailPipeBttm);
            } catch (error) {
                console.error('Error calculating Tail Pipe Bottom:', error);
                return wellSurvey.setTailPipeBottom(0);
            }
        })
    );
};

// Auto-load well survey data when project is selected
const autoLoadWellSurveyEpic = (action$: any, state$: any) =>
    action$.pipe(
        ofType("myProjects/setSelectedProject"),
        debounceTime(300),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject))
        ),
        switchMap(([action, selectedProject]) => {
            if (selectedProject?.projectID || selectedProject?.caseID) {
                return of({ type: "jetlift/wellSurvey/fetchExistingData" });
            }
            return of({ type: "NOOP" });
        })
    );

// Update the combineEpics export
const saveWellSurveyEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/wellSurvey/saveData"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey)),
            state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject))
        ),
        switchMap(([actionData, wellSurveyState, selectedProject]) => {
            const projectId = selectedProject?.projectID || selectedProject?.caseID;
            
            if (!projectId) {
                return of({ type: "jetlift/wellSurvey/saveDataError", payload: "No project ID found" });
            }

            const wellSurveyMetadata = {
                KOPOverride: wellSurveyState?.KOPOverride,
                MaxDLSAboveKOP: wellSurveyState?.MaxDLSAboveKOP,
                DepthOfMaxDLS: wellSurveyState?.DepthOfMaxDLS,
                TransitionZone: wellSurveyState?.TransitionZone,
                ESPLength: wellSurveyState?.ESPLength,
                BottomOfESPSensor: wellSurveyState?.BottomOfESPSensor,
                TailPipeDesander: wellSurveyState?.TailPipeDesander,
                ShowWellDataInMainReport: wellSurveyState?.showWellDataInMainReport,
                Notes: wellSurveyState?.wellSurvey?.notes
            };

            const surveyData = wellSurveyState?.wellSurveyData?.map((item: any) => ({
                MD: item.md,
                INC: item.inc,
                DLS: item.dls
            })) || [];

            const payload = {
                projectId,
                wellSurveyMetadata,
                surveyData
            };

            return from(wellSurveyService.saveWellSurveyData(payload)).pipe(
                map(() => ({ type: "jetlift/wellSurvey/saveDataSuccess" })),
                catchError((error) => {
                    console.error('Error saving well survey data:', error);
                    return of({ type: "jetlift/wellSurvey/saveDataError", payload: error.message });
                })
            );
        })
    );
};

export const jetLiftWellSurveyEpic = combineEpics(
    fetchWellSurveyEpic,
    clearWellSurveyEpic,
    calculateMaxDLS,
    calculateDepthMaxDLS,
    calculateTransitionZone,
    calculateTailPipeTZ,
    fetchExistingWellSurveyEpic,
    calculateTailPipeBttmLength,
    autoLoadWellSurveyEpic,
    saveWellSurveyEpic
);
